/**
 * Reset Password Dialog
 * Admin dialog to reset a user's password
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import type { AdminResetPasswordRequest, ManagedUser } from '@/types/user-management'

// ============================================================================
// HELPERS
// ============================================================================

function validatePassword(pw: string): string | null {
  if (pw.length < 8) return 'At least 8 characters required'
  if (!/[A-Z]/.test(pw)) return 'Must contain uppercase letter'
  if (!/[a-z]/.test(pw)) return 'Must contain lowercase letter'
  if (!/[0-9]/.test(pw)) return 'Must contain a number'
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Must contain a special character'
  return null
}

// ============================================================================
// COMPONENT
// ============================================================================

interface ResetPasswordDialogProps {
  user: ManagedUser | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: AdminResetPasswordRequest) => void
  isPending: boolean
}

export function ResetPasswordDialog({
  user,
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: ResetPasswordDialogProps) {
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const err = validatePassword(newPassword)
    if (err) {
      setError(err)
      return
    }
    onSubmit({ newPassword, mustChangePassword: true })
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setNewPassword('')
      setError(null)
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>
            {user ? `Reset password for ${user.email}` : 'Reset user password'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="newPassword">New Password *</Label>
            <Input
              id="newPassword"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(null) }}
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
